import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { outdoorAdventurePanels } from "@/data/resort";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";

export function OutdoorAdventureStory() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref });
  const count = outdoorAdventurePanels.length;
  const x = useTransform(scrollYProgress, [0, 1], ["0%", `-${((count - 1) / count) * 100}%`]);

  return (
    <section
      id="outdoor-story"
      aria-labelledby="outdoor-story-heading"
      className="bg-forest-deep text-primary-foreground"
    >
      <div className="shell pt-16 md:pt-20">
        <Reveal>
          <Eyebrow tone="light">Go Outside</Eyebrow>
        </Reveal>
        <h2 id="outdoor-story-heading" className="display-lg mt-6 max-w-[16ch]">
          <MaskReveal>The Sierra Foothills</MaskReveal>
          <MaskReveal delay={0.08}>Are Calling</MaskReveal>
        </h2>
      </div>

      <div ref={ref} className="relative mt-8 hidden h-[420vh] lg:block">
        <div className="sticky top-0 h-screen overflow-hidden">
          <motion.div
            className="flex h-full"
            style={{ width: `${count * 100}%`, x: reduced ? 0 : x }}
          >
            {outdoorAdventurePanels.map((panel) => (
              <article key={panel.id} className="relative h-full w-full shrink-0">
                <img
                  src={panel.image}
                  alt={panel.alt}
                  width={1600}
                  height={1200}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 size-full object-cover"
                />
                <div className="overlay-cinematic absolute inset-0" />
                <div className="shell relative flex h-full flex-col justify-end pb-16">
                  <h3 className="font-display text-[clamp(4rem,11vw,10rem)] uppercase leading-[0.85] tracking-[-0.04em]">
                    {panel.word}
                  </h3>
                  <p className="mt-6 max-w-xl text-lg text-primary-foreground/80">{panel.sub}</p>
                </div>
              </article>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="space-y-6 px-5 pb-16 pt-8 lg:hidden">
        {outdoorAdventurePanels.map((panel, i) => (
          <Reveal key={panel.id} delay={i * 0.04}>
            <article className="relative aspect-[4/5] overflow-hidden">
              <img
                src={panel.image}
                alt={panel.alt}
                width={1600}
                height={1200}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 size-full object-cover"
              />
              <div className="overlay-cinematic absolute inset-0" />
              <div className="relative flex h-full flex-col justify-end p-7">
                <h3 className="font-display text-6xl uppercase leading-[0.85] tracking-[-0.04em]">
                  {panel.word}
                </h3>
                <p className="mt-3 text-sm text-primary-foreground/80">{panel.sub}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
